import { useEffect } from "react";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  BadgeCheck,
  ShieldCheck,
  Clock3,
  Sparkles,
  Check,
} from "lucide-react";
import { CONTACT, ASSETS } from "@/lib/constants";
import { useApplyIntent } from "@/components/ApplyIntent";
import { useMLO } from "@/lib/mloContext";
import { resolveApplyUrl, telHref } from "@/lib/applyTarget";

function usePageMeta(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let el = document.head.querySelector('meta[name="description"]');
    const prevDesc = el ? el.getAttribute("content") : null;
    if (el) el.setAttribute("content", description);
    return () => {
      document.title = prev;
      if (el && prevDesc !== null) el.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

// Renders the apply CTA — or a phone call when this MLO hasn't added their
// application link yet, so we never send their borrower somewhere else.
const ApplyButton = ({ testid, className = "", children, opts }) => {
  const { openIntent } = useApplyIntent();
  if (!opts.applyUrl && opts.mloPhone) {
    return (
      <a data-testid={testid} href={telHref(opts.mloPhone)} className={className}>
        <Phone size={17} /> Call {opts.firstName}
      </a>
    );
  }
  return (
    <button type="button" data-testid={testid} onClick={() => openIntent(testid, opts)} className={className}>
      {children}
    </button>
  );
};

export default function Apply() {
  const mlo = useMLO();
  const fullName = mlo?.name || CONTACT.loanOfficer;
  const firstName = fullName.split(" ")[0];
  const nmls = mlo?.nmls || CONTACT.nmls;
  const phoneDisplay = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone ? telHref(mlo.phone) : CONTACT.phoneHref;
  const email = mlo?.email || (mlo ? "" : CONTACT.email);
  const years = mlo ? mlo.years_experience : 30;
  const yearsPhrase = years ? `${years}+ years of mortgage experience` : "years of mortgage experience";
  const photo = mlo ? mlo.photoSecondaryUrl || mlo.photoUrl : ASSETS.headshotApply;
  const applyUrl = resolveApplyUrl(mlo);
  const opts = { applyUrl, mloSlug: mlo?.slug, mloName: mlo?.name, mloPhone: mlo?.phone, firstName };

  usePageMeta(
    `Apply Now with ${fullName} | Great Rate Check`,
    `Start your mortgage application with ${fullName}, NMLS #${nmls}, at NEXA Lending. Friendly, direct, and built around a great rate.`
  );

  return (
    <div data-testid="apply-page" className="min-h-screen bg-linen text-navy">
      {/* Top bar */}
      <header className="absolute top-0 inset-x-0 z-40">
        <div className="container-grc flex items-center justify-between h-16 sm:h-20">
          <a
            href={mlo ? `/${mlo.slug}` : "/"}
            data-testid="apply-nav-logo"
            className="inline-flex items-center rounded-2xl bg-white/95 backdrop-blur px-3 py-1.5 shadow-soft"
          >
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-10 w-auto" />
          </a>
          <a
            data-testid="apply-nav-call"
            href={phoneHref}
            className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium text-sm"
          >
            <Phone size={16} className="text-lime" /> {phoneDisplay}
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid="apply-hero"
        className="relative overflow-hidden pt-24 sm:pt-28 pb-16 sm:pb-24 grain-bg noise text-white"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="container-grc relative grid grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Copy */}
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-lime/90">
              <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
              NEXA Lending · NMLS #{CONTACT.companyNmls}
            </span>

            <h1 className="mt-6 font-heading font-medium text-balance text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-[4rem] lg:leading-[1.02]">
              Let's find the <span className="italic text-lime">perfect loan</span> for you.
            </h1>

            <p className="mt-6 max-w-xl text-lg sm:text-xl text-white/80 leading-relaxed">
              Start your application in minutes and work one-on-one with {fullName} —
              {years ? ` ${yearsPhrase},` : ""} a friendly approach, and a relentless
              focus on getting you a great rate.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <ApplyButton
                testid="apply-hero-cta"
                opts={opts}
                className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
              >
                Apply Now <ArrowRight size={18} />
              </ApplyButton>
              <a
                data-testid="apply-hero-call"
                href={phoneHref}
                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors"
              >
                <Phone size={16} /> Talk to {firstName} first
              </a>
            </div>

            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-white/70">
              <li className="inline-flex items-center gap-2">
                <ShieldCheck size={16} className="text-teal" strokeWidth={1.75} /> Secure & encrypted
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock3 size={16} className="text-lime" strokeWidth={1.75} /> ~10 minutes to apply
              </li>
              <li className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-teal" strokeWidth={1.75} /> No obligation
              </li>
            </ul>
          </div>

          {/* Loan officer photo — capped near native resolution to stay sharp */}
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2 flex lg:justify-center">
            <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[300px]">
              <div className="absolute -inset-5 rounded-[2.5rem] bg-teal/25 blur-3xl" aria-hidden />
              <div className="absolute -bottom-4 -right-4 w-40 h-40 rounded-full bg-lime/20 blur-2xl" aria-hidden />
              <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/15 shadow-lift bg-navy-light">
                {photo ? (
                  <img
                    src={photo}
                    alt={`${fullName}, mortgage loan officer at NEXA Lending`}
                    width={300}
                    height={375}
                    className="w-full h-full object-cover aspect-[4/5]"
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full aspect-[4/5] flex items-center justify-center text-white/40 font-heading text-6xl">
                    {firstName.slice(0, 1)}
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-deep/90 to-transparent p-5 pt-14">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="font-heading font-medium text-xl text-white leading-tight">{fullName}</p>
                      <p className="text-xs text-white/70">
                        {mlo?.title || "Loan Officer"} · NMLS #{nmls}
                      </p>
                    </div>
                    {years ? (
                      <span className="inline-flex items-center gap-1.5 bg-lime text-navy-deep text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                        <BadgeCheck size={14} /> {years}+ yrs
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-linen/30" />
      </section>

      {/* Why this loan officer */}
      <section data-testid="apply-why" className="py-16 sm:py-20 bg-white">
        <div className="container-grc grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Straight-talking guidance",
              body: `No jargon, no pressure. ${firstName} explains your options in plain English so you can decide with confidence.`,
            },
            {
              title: "A great rate, shopped for you",
              body: `Through NEXA's wholesale lender network, ${firstName} compares programs to find the rate that fits your goals.`,
            },
            {
              title: "One dedicated point of contact",
              body: `From first question to closing day, you work directly with ${firstName} — not a rotating call center.`,
            },
          ].map((c, i) => (
            <article
              key={i}
              data-testid={`apply-why-${i}`}
              className="rounded-3xl bg-linen border border-linen-border p-8 shadow-soft"
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
                <Check size={22} strokeWidth={2} />
              </div>
              <h3 className="mt-5 font-heading font-medium text-xl">{c.title}</h3>
              <p className="mt-2 text-ink-600 leading-relaxed">{c.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Contact + Apply card */}
      <section data-testid="apply-contact" className="py-16 sm:py-20 bg-linen">
        <div className="container-grc">
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft overflow-hidden grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-7 p-8 sm:p-12">
              <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">Ready when you are</p>
              <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl text-navy text-balance">
                Start your application — {firstName} takes it from there.
              </h2>
              <p className="mt-4 text-ink-600 leading-relaxed max-w-xl">
                {applyUrl
                  ? "The secure application is powered by NEXA Lending. It only takes about ten minutes, and there's no obligation to move forward."
                  : `Give ${firstName} a call and you'll be walked through the application start to finish — about ten minutes, and no obligation to move forward.`}
              </p>
              <div className="mt-8">
                <ApplyButton
                  testid="apply-contact-cta"
                  opts={opts}
                  className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-base px-8 py-4 rounded-full shadow-lift transition-all hover:-translate-y-0.5"
                >
                  Apply Now <ArrowRight size={18} />
                </ApplyButton>
              </div>
            </div>

            <div className="lg:col-span-5 bg-navy text-white p-8 sm:p-12 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-teal/20 blur-3xl" aria-hidden />
              <div className="relative">
                <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">Your loan officer</p>
                <p className="mt-3 font-heading font-medium text-2xl">{fullName}</p>
                <p className="text-sm text-white/70">NMLS #{nmls}</p>

                <ul className="mt-6 space-y-4 text-sm">
                  <li>
                    <a data-testid="apply-contact-phone" href={phoneHref} className="flex items-center gap-3 hover:text-lime transition-colors">
                      <Phone size={16} className="text-lime shrink-0" /> {phoneDisplay}
                    </a>
                  </li>
                  {email ? (
                    <li>
                      <a data-testid="apply-contact-email" href={`mailto:${email}`} className="flex items-center gap-3 hover:text-lime transition-colors break-all">
                        <Mail size={16} className="text-lime shrink-0" /> {email}
                      </a>
                    </li>
                  ) : null}
                  <li className="flex items-start gap-3 text-white/80">
                    <MapPin size={16} className="text-lime shrink-0 mt-0.5" />
                    <span>
                      {mlo ? mlo.city || CONTACT.cityStateZip : (
                        <>
                          {CONTACT.addressLine}
                          <br />
                          {CONTACT.cityStateZip}
                        </>
                      )}
                    </span>
                  </li>
                  <li className="flex items-center gap-3 text-white/80">
                    <BadgeCheck size={16} className="text-lime shrink-0" /> {CONTACT.company} · NMLS #{CONTACT.companyNmls}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="apply-final-cta" className="relative py-20 sm:py-24 overflow-hidden grain-bg noise text-white">
        <div className="container-grc relative text-center max-w-3xl">
          <h2 className="font-heading font-medium text-4xl sm:text-5xl leading-[1.05] text-balance">
            Your <span className="italic text-lime">great rate</span> starts here.
          </h2>
          <p className="mt-5 text-lg text-white/75">
            {applyUrl ? `Apply now, or call ${firstName} directly — whichever feels right.` : `Call ${firstName} directly and get started in minutes.`}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <ApplyButton
              testid="apply-final-cta-button"
              opts={opts}
              className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-9 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
            >
              Apply Now <ArrowRight size={18} />
            </ApplyButton>
            <a
              data-testid="apply-final-call"
              href={phoneHref}
              className="text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors inline-flex items-center gap-2"
            >
              <Phone size={16} /> Call {phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-deep text-white/70 py-10">
        <div className="container-grc space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="inline-flex items-center rounded-xl bg-white/95 px-3 py-1.5">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-7 w-auto" />
            </div>
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} Great Rate Check · Equal Housing Lender
            </p>
          </div>
          <p className="text-[11px] leading-relaxed text-white/50">
            {fullName}, NMLS #{nmls}, is a licensed mortgage loan originator with NEXA Mortgage LLC
            (NMLS #{CONTACT.companyNmls}), an Equal Housing Lender. {CONTACT.addressLine}, {CONTACT.cityStateZip}.
            This is not a commitment to lend. All loans subject to credit approval. Rates and terms subject to
            change without notice.
          </p>
        </div>
      </footer>
    </div>
  );
}
